import type { Language, Subsidiary } from '@coolblue-development/constants';
import { ProductTradeInBrandsData } from '@features/product-page/api/trade-in/getBrands';
import { ProductTradeInProductImagesData } from '@features/product-page/api/trade-in/getProductImages';

export type NormalizedAllInfoResponse = {
  defaultSettings: TradeInSettings | null;
  settingsBrands: BrandPair;
  options: MappedOption[];
  variances: { [key: number]: VarianceV2[] };
  optionsBrands: BrandPair;
  brandData: ProductTradeInBrandsData;
  productImages: ProductTradeInProductImagesData | null;
} | null;

export type AllInfoResponse = {
  defaultSettings: TradeInSettings | null;
  settingsBrands: BrandPair;
  options: OptionPair;
  variances: { [key: number]: VarianceV2[] };
  optionsBrands: BrandPair;
} | null;

export type BrandPair = { [key: number]: Brand };
export type OptionPair = { [key: number]: TradableOption };

export type TradeInSettings = {
  productTypeId: number;
  brandId: number | null;
  enabled: boolean;
  title: string | null;
  explanation: string | null;
  genericText: string | null;
  storeInstructions: { instructions: string };
  onlineInstructions: { instructions: string };
};

export type Variance = {
  varianceId: number;
  value: string;
  title: string;
  sortOrder: number;
  showImage: boolean;
};

export type Option = {
  id?: number;
  brandId?: number;
  productId: number;
  priceLevel1: number;
  priceLevel2: number;
  priceLevel3: number;
  productImageId?: number;
  variances: { [key: string]: Variance };
};

export type TradableOption = {
  optionId: number;
  brandId: number;
  priceLevelOne: number;
  priceLevelTwo: number;
  priceLevelThree: number;
  productIdForImage: number;
  productId: number;
  variances: { [key: string]: Variance };
};

export type MappedOption = {
  variances: Variance[];
  optionId: number;
  brandId: number;
  priceLevelOne: number;
  priceLevelTwo: number;
  priceLevelThree: number;
  productIdForImage: number;
  productId: number;
};

export type Question = {
  productTypeId: number;
  brandId: number;
  questionId: number;
  question: string;
  explanation: string;
  sortOrder: number;
  answerYes: string;
  answerNo: string;
};

export type Brand = {
  brandId: number;
  settings: TradeInSettings;
  questions: Question[];
  name?: string;
};

export type VarianceV2 = {
  id?: number;
  varianceTitle: string;
  reference: string;
  showAllFilterValues: 0 | 1;
  language: Language;
  showImage: 0 | 1;
  imageMapping?: string;
};

export type Summary = { tradeInPrice: number; priceLevel: string };

export type TradeInDeviceCondition = {
  grade: string | null;
  technical: string | null;
  visual: string | null;
};

export type TradeInGradingResult = {
  condition: TradeInDeviceCondition;
  price: number;
};

export enum Grade {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E'
}

export enum DeviceStorage {
  STORAGE_32GB = 'STORAGE_32GB',
  STORAGE_64GB = 'STORAGE_64GB',
  STORAGE_128GB = 'STORAGE_128GB',
  STORAGE_256GB = 'STORAGE_256GB',
  STORAGE_512GB = 'STORAGE_512GB',
  STORAGE_1TB = 'STORAGE_1TB',
  STORAGE_2TB = 'STORAGE_2TB'
}

export type TradeInAnswerInput = {
  questionTagName: string;
  questionGroupName: string;
  questionId: number;
  grade: Grade;
};

export type DeviceInput = {
  model: string;
  storage: DeviceStorage;
};

export type CalculateTradeInEstimateInput = {
  answers: TradeInAnswerInput[];
  device: DeviceInput;
  language: Language;
  subsidiary: Subsidiary;
};

export type StepArgument = 'next' | 'previous' | number;
export type BinaryAnswer = 'yes' | 'no';
export type FreeFormAnswer = string;
export type Answer = BinaryAnswer | FreeFormAnswer;

export type ExplanationInfo = {
  explanation: string;
  title: string;
};

export type EnhancedQuestionImage = {
  id: string;
};

export type EnhancedQuestionGroup = {
  id: string;
  name: string;
  title: string;
  tag?: string;
  hint?: string;
  isSingleAnswer?: boolean;
};

export type EnhancedOption = {
  tag: string;
  title: string;
  text: string;
  hint?: string;
  order: number;
  image?: EnhancedQuestionImage | null;
};

export enum EnhancedQuestionDisplayType {
  SINGLE_SELECTION = 'SINGLE_SELECTION',
  MULTI_SELECTION = 'MULTI_SELECTION'
}

export type EnhancedAnswer = {
  optionOrder: number;
  optionTag: string;
  questionGroupName?: string;
};

export type EnhancedQuestion = {
  id: string;
  title: string;
  text: string;
  order: number;
  displayType: EnhancedQuestionDisplayType;
  hint?: string | null;
  image?: EnhancedQuestionImage | null;
  group?: EnhancedQuestionGroup;
  options: EnhancedOption[];
};

export type State = {
  fetched: boolean;
  isModalOpen?: boolean;
  productTypeId?: number;
  productType?: string;
  salesPrice?: number;
  isLoading?: boolean;
  steps?: {
    completed: boolean | undefined;
  }[];
  goToStep?: (stepToSet: StepArgument) => void;
  currentStep?: number;
  entry?: NormalizedAllInfoResponse;
  brands?: { [key: number]: Brand };
  selectedBrand?: number;
  options?: MappedOption[];
  selectedOption?: string | null;
  variantChoices?: string[];
  selectedVariant?: number | null;
  updateSelectedVariant?: (optionId: number | null) => void;
  questions?: Question[];
  answers: { [key: number]: Answer };
  summary?: Summary | null;
  settings?: TradeInSettings;
  brandData?: ProductTradeInBrandsData;
  entranceChoice?: boolean;
  explanationInfo?: ExplanationInfo | null;
  isExplanationModalOpen?: boolean;
  handleAnswerChange?: (
    question: { question: Question['question'] },
    answer: Answer
  ) => void | ((question: Question, answer: Answer) => void);
  enhancedQuestions?: EnhancedQuestion[];
  enhancedAnswers?: Map<string, EnhancedAnswer>;
  currentEnhancedQuestion?: EnhancedQuestion;
  answeredEnhancedQuestionsIds?: Set<string>;
  isEnhancedQuestionsLoading?: boolean;
  isEnhancedQuestionsError?: boolean;
  enhancedResult?: TradeInGradingResult | null;
  isEnhancedResultLoading?: boolean;
  isEnhancedResultError?: boolean;
  isEnhancedQuestionnaire?: boolean;
};

export enum Actions {
  SET_MODAL_OPEN = 'SET_MODAL_OPEN',
  SET_PRODUCT_TYPE_ID = 'SET_PRODUCT_TYPE_ID',
  SET_PRODUCT_TYPE = 'SET_PRODUCT_TYPE',
  SET_SALES_PRICE = 'SET_SALES_PRICE',
  SET_LOADING = 'SET_LOADING',
  SET_ENTRY = 'SET_ENTRY',
  SET_CURRENT_STEP = 'SET_CURRENT_STEP',
  SET_SELECTED_BRAND = 'SET_SELECTED_BRAND',
  SET_SELECTED_VARIANT = 'SET_SELECTED_VARIANT',
  SET_VARIANT_CHOICES = 'SET_VARIANT_CHOICES',
  SET_SELECTED_OPTION = 'SET_SELECTED_OPTION',
  SET_ANSWER = 'SET_ANSWER',
  SET_ANSWERS = 'SET_ANSWERS',
  SET_SUMMARY = 'SET_SUMMARY',
  SET_ENTRANCE_CHOICE = 'SET_ENTRANCE_CHOICE',
  SET_EXPLANATION_INFO = 'SET_EXPLANATION_INFO',
  SET_EXPLANATION_MODAL = 'SET_EXPLANATION_MODAL',
  SET_ENHANCED_QUESTIONS = 'SET_ENHANCED_QUESTIONS',
  SET_ENHANCED_ANSWER = 'SET_ENHANCED_ANSWER',
  SET_CURRENT_ENHANCED_QUESTION = 'SET_CURRENT_ENHANCED_QUESTION',
  SET_ENHANCED_QUESTIONS_LOADING = 'SET_ENHANCED_QUESTIONS_LOADING',
  SET_ENHANCED_QUESTIONS_ERROR = 'SET_ENHANCED_QUESTIONS_ERROR',
  SET_ENHANCED_RESULT = 'SET_ENHANCED_RESULT',
  SET_ENHANCED_RESULT_LOADING = 'SET_ENHANCED_RESULT_LOADING',
  SET_ENHANCED_RESULT_ERROR = 'SET_ENHANCED_RESULT_ERROR',
  SET_ENHANCED_QUESTIONNAIRE = 'SET_ENHANCED_QUESTIONNAIRE'
}

export type Action =
  | { type: Actions.SET_MODAL_OPEN; payload: boolean }
  | { type: Actions.SET_PRODUCT_TYPE_ID; payload: number }
  | { type: Actions.SET_PRODUCT_TYPE; payload: string }
  | { type: Actions.SET_SALES_PRICE; payload: number }
  | { type: Actions.SET_LOADING; payload: boolean }
  | { type: Actions.SET_ENTRY; payload: NormalizedAllInfoResponse }
  | { type: Actions.SET_CURRENT_STEP; payload: number }
  | { type: Actions.SET_SELECTED_BRAND; payload: number }
  | { type: Actions.SET_SELECTED_VARIANT; payload: number | null }
  | { type: Actions.SET_VARIANT_CHOICES; payload: string[] }
  | { type: Actions.SET_SELECTED_OPTION; payload: string | null }
  | { type: Actions.SET_ANSWER; payload: { questionId?: number; answer: Answer } }
  | { type: Actions.SET_ANSWERS; payload: { [key: number]: Answer } }
  | { type: Actions.SET_SUMMARY; payload: Summary | null }
  | { type: Actions.SET_ENTRANCE_CHOICE; payload: boolean }
  | { type: Actions.SET_EXPLANATION_INFO; payload: ExplanationInfo }
  | { type: Actions.SET_EXPLANATION_MODAL; payload: boolean }
  | { type: Actions.SET_ENHANCED_QUESTIONS; payload: EnhancedQuestion[] }
  | { type: Actions.SET_ENHANCED_ANSWER; payload: { questionId: string; answer: EnhancedAnswer } }
  | { type: Actions.SET_CURRENT_ENHANCED_QUESTION; payload: EnhancedQuestion }
  | { type: Actions.SET_ENHANCED_QUESTIONS_LOADING; payload: boolean }
  | { type: Actions.SET_ENHANCED_QUESTIONS_ERROR; payload: boolean }
  | { type: Actions.SET_ENHANCED_RESULT; payload: TradeInGradingResult | null }
  | { type: Actions.SET_ENHANCED_RESULT_LOADING; payload: boolean }
  | { type: Actions.SET_ENHANCED_RESULT_ERROR; payload: boolean }
  | { type: Actions.SET_ENHANCED_QUESTIONNAIRE; payload: boolean };
